export {Home as Page};

import messages from "../../messages/messages.js";
import API_URL from "../../includes/api.js";
import {useEffect, useRef, useState} from "preact/hooks";
import {Playlist} from "../../components/Playlist.jsx";
import {Helmet} from 'react-helmet-async';
import ReactPlayer from "react-player/youtube";
import {ArrowRightIcon, ChevronRightIcon, PlayIcon, VideoCameraIcon} from "@heroicons/react/24/solid";
import {usePageContext} from "../../hooks/usePageContext.js";

function Home() {
    const iframeRef = useRef(null);
    const playerRef = useRef(null);

    // Seeded from +data.shared.js's server-side fetch so the grid isn't empty
    // until the client-side fetch below completes after hydration.
    const seededPlaylists = usePageContext()?.data?.playlists ?? null;

    const [playlists, setPlaylists] = useState(seededPlaylists ?? []);
    const [shouldLoadIframe, setShouldLoadIframe] = useState(false);
    const [shouldLoadPlayer, setShouldLoadPlayer] = useState(false);

    useEffect(() => {
        if (!seededPlaylists) {
            fetch(API_URL + "/playlist/all?home=true")
                .then(response => response.json()).then(data => {
                if (data.code === 200) {
                    setPlaylists(data.data);
                }
            });
        }

        if ('IntersectionObserver' in window) {
            const observer = new IntersectionObserver(
                (entries) => {
                    entries.forEach(entry => {
                        if (entry.isIntersecting) {
                            if (entry.target === iframeRef.current) {
                                setShouldLoadIframe(true);
                                observer.unobserve(entry.target);
                            }
                            if (entry.target === playerRef.current) {
                                setShouldLoadPlayer(true);
                                observer.unobserve(entry.target);
                            }
                        }
                    });
                },
                {threshold: 0.1}
            );

            if (iframeRef.current) observer.observe(iframeRef.current);
            if (playerRef.current) observer.observe(playerRef.current);

            return () => {
                if (iframeRef.current) observer.unobserve(iframeRef.current);
                if (playerRef.current) observer.unobserve(playerRef.current);
            };
        } else {
            setShouldLoadIframe(true);
            setShouldLoadPlayer(true);
        }
    }, [])

    return (<>

            <Helmet>
                <title>{messages["META.HOME.TITLE"]}</title>
                <meta name="description" content={messages["META.HOME.DESC"]}/>
            </Helmet>

            <section className="relative pt-20 py-15 md:py-40">
                <picture>
                    <source
                        srcSet={"/img/" + messages["HERO.IMAGE.PHONE"]}
                        media="(max-width: 640px)"
                    />
                    <source
                        srcSet={"/img/" + messages["HERO.IMAGE"]}
                        media="(min-width: 641px)"
                    />
                    <img
                        src={"/img/" + messages["HERO.IMAGE.PHONE"]}
                        alt={messages["HERO.ALT"] || "Hero image"}
                        loading="eager"
                        fetchpriority="high"
                        width="1920"
                        height="1080"
                        className="absolute inset-0 w-full h-full object-cover z-0"
                    />
                </picture>

                <div className="absolute inset-0 header-home z-1 max-w-[unset]"/>

                <div className="relative z-2 grid md:grid-cols-2 gap-10">
                    <div class="space-y-6">

                        <a href={messages["WHAT_IS.URL"]} aria-label={messages["HERO.LINK"]}
                           className="inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs text-gray-400">
                            <span className="h-2 w-2 rounded-full bg-red-400"
                                  aria-hidden="true"></span>{messages["HERO.LINK"]}
                        </a>

                        <h1 className="text-4xl md:text-6xl tracking-tight leading-tight">{messages["HERO.TITLE"]}</h1>

                        <p>{messages["HERO.SUBTITLE"]}</p>

                        <div className="flex flex-wrap gap-3">
                            <a href="/playlists"
                               className="primary-btn">
                                Explore Playlists
                            </a>
                            <a href={messages["DISCORD_URL"]} target={"_blank"} rel="noopener noreferrer"
                               className="secondary-btn">
                                Join Discord
                            </a>
                        </div>

                        <div className="flex gap-4 md:gap-6 pt-2 text-sm text-gray-400">
                            <div><span className="text-white">HD</span> video</div>
                            <div>Over <span className="text-white">{messages["SONG.COUNT"]}</span> songs</div>
                            <div><span className="text-white">Hourly</span> drops</div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="z-1 relative">
                <div>
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-5 gap-3">
                        <div>
                            <h2 className="text-white mb-2">{messages["SITE.NAME"]} Playlists</h2>
                            <p className="mb-0">Enjoy our
                                exclusive {messages["SITE.NAME"]} playlists! New
                                songs are added every day.
                            </p>
                        </div>

                        <a href="/playlists" className="text-gray-400 hover:text-white text-sm">
                            All playlists <ChevronRightIcon className="inline-block h-4 w-4 mb-1"/>
                        </a>

                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-5 mb-8 text-center">
                        {playlists.map((playlist) => (<Playlist playlist={playlist}/>))}
                    </div>
                </div>
            </section>

            <section>
                <div>
                    {messages['INTRO_VIDEO_HOME'] ? (<div className="flex flex-col md:flex-row gap-8">
                        <div ref={playerRef} className="md:w-5/12 rounded-lg border border-border/60 bg-zinc-800">
                            {shouldLoadPlayer && (
                                <ReactPlayer
                                    className="w-full group max-w-full !h-50 md:!h-80"
                                    iframeClassName={"rounded-lg w-full"}
                                    url={`https://www.youtube-nocookie.com/watch?v=` + messages['INTRO_VIDEO_HOME']}
                                    light={'/img/intro-video-thumbnail.webp'}
                                    playing
                                    playIcon={<PlayIcon className="w-20 h-20 z-1 hover:scale-120 transition-transform duration-300 group-hover:scale-120 text-white"/>}
                                        volume={0.35}
                                    config={{
                                        youtube: {
                                            playerVars: {
                                                controls: 0,
                                                rel: 0,
                                                autoplay: 1,
                                                start: 0,
                                                modestbranding: 1,
                                                loop: 1,
                                            }
                                        }
                                    }}
                                />
                            )}
                        </div>
                        <div className="md:w-7/12 flex flex-col justify-center"
                             dangerouslySetInnerHTML={{__html: messages["HOME.PASSION"]}}>
                        </div>
                    </div>) : (<div dangerouslySetInnerHTML={{__html: messages["HOME.PASSION"]}}>
                    </div>)}
                </div>
            </section>

            <section className="join-network">
                <div className="rounded-lg border border-border/60 bg-zinc-800 py-4 px-4 md:px-8">
                    <h2>Join our network!</h2>
                    <div dangerouslySetInnerHTML={{__html: messages["HOME.COMMUNITY"]}}></div>
                </div>
            </section>

            <section>
                <div>
                    <h2>The Community on Discord!</h2>

                    <div className="text-white flex flex-col md:flex-row gap-10 items-center">
                        <div>
                            <p><b>Our discord server is the hub for {messages["SITE.NAME"]} lovers and creators! Whether
                                you're a devoted
                                fan or a {messages["SITE.NAME"]} artist looking to grow, our community is here to
                                support you.</b></p>

                            <h3>What We Offer:</h3>

                            <ul>
                                <li>A vibrant place full of passionate {messages["SITE.NAME"]} fans and creators.</li>
                                <li>Collaboration opportunities for YouTube channels.</li>
                                <li>Tips for improving your videos, thumbnails, and content.</li>
                                <li>A place to share and discover the best {messages["SITE.NAME"]} tracks.</li>
                            </ul>
                            <br/>

                            <p>We aim to unite the global {messages["SITE.NAME"]} community. Together, we grow, learn,
                                and succeed in
                                sharing our love for {messages["SITE.NAME"]}. Whether you're looking to connect,
                                collaborate, or
                                simply enjoy the
                                music, we've got you covered.</p>

                            <p>Join us on Discord and take your {messages["SITE.NAME"]} journey to the next level!</p>

                            <a href={messages["DISCORD_URL"]} target={"_blank"} rel="noopener noreferrer"
                               className="primary-btn">
                                Join Discord
                            </a>

                        </div>

                        <div ref={iframeRef} style={{minHeight: 450, minWidth: 300}}>
                            {shouldLoadIframe ? (
                                <iframe
                                    src={"https://discord.com/widget?id=" + messages['DISCORD_WIDGET_ID'] + "&theme=dark"}
                                    height="450"
                                    title="Discord server widget"
                                    className="max-w-full"
                                    allowTransparency="true" frameBorder="0"
                                    sandbox="allow-popups allow-popups-to-escape-sandbox allow-same-origin allow-scripts"></iframe>
                            ) : null}
                        </div>

                    </div>
                </div>
            </section>
        </>
    );
}
